import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import html2canvas from "html2canvas";
import { Star, Heart, Bookmark, BookmarkCheck, RefreshCw, X, Share2, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  { id: "strength", label: "Strength", icon: Star, color: "#C86B55", gradient: "linear-gradient(135deg, #C17D3C, #E8A87C)" },
  { id: "truth", label: "Truth", icon: Heart, color: "#4A6B53", gradient: "linear-gradient(135deg, #5B8A6F, #8EC4A8)" },
  { id: "heart", label: "Heart", icon: Heart, color: "#E5B95F", gradient: "linear-gradient(135deg, #B56576, #D4A5B5)" },
];

export default function AffirmationsPage() {
  const [activeTab, setActiveTab] = useState("daily");
  const [card, setCard] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFaved, setIsFaved] = useState(false);
  const [showShareWarning, setShowShareWarning] = useState(false);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  const fetchCard = useCallback(async (cat) => {
    try {
      const params = cat ? `?category=${cat}` : "";
      const res = await api.get(`/affirmation/daily${params}`);
      setCard(res.data); setIsFaved(false);
    } catch (e) { console.error(e); }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try { const res = await api.get(`/affirmation/favorites`); setFavorites(res.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchCard(selectedCategory); fetchFavorites(); }, [fetchCard, fetchFavorites, selectedCategory]);

  const saveToFavorites = async () => {
    if (!card) return;
    try { await api.post(`/affirmation/favorite`, { text: card.text, category: card.category }); setIsFaved(true); fetchFavorites(); } catch (e) { console.error(e); }
  };

  const removeFavorite = async (id) => {
    try { await api.delete(`/affirmation/favorite/${id}`); fetchFavorites(); } catch (e) { console.error(e); }
  };

  const handleShare = () => { setShowShareWarning(true); };

  const confirmShare = async () => {
    setShowShareWarning(false);
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      const dataUrl = canvas.toDataURL("image/png");

      // Try native share on mobile (Safari, mobile Chrome)
      let shared = false;
      if (navigator.share) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], "sovha-affirmation.png", { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "A word for your soul", text: "From Sovha" });
            shared = true;
          }
        } catch (shareErr) {
          // Share cancelled or not supported — fall through to download
          if (shareErr.name !== "AbortError") console.error(shareErr);
        }
      }

      // Fallback: download as PNG (desktop Chrome, Firefox, etc.)
      if (!shared) {
        const link = document.createElement("a");
        link.download = "sovha-affirmation.png";
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) { console.error("Share error:", e); }
    setSharing(false);
  };

  const catInfo = CATEGORIES.find((c) => c.id === card?.category);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="affirmations-page">
      <div className="text-center mb-12">
        <p className="text-warmyellow text-sm uppercase tracking-[0.15em] font-bold mb-3">Affirmations</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">Words for your soul</h1>
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-2 mb-10" data-testid="affirmation-tabs">
        {[{ id: "daily", label: "Daily Card" }, { id: "favorites", label: "Favorites" }].map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`affirmation-tab-${t.id}`}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === t.id ? "bg-warmyellow text-earth-800" : "bg-earth-200/50 text-earth-600 hover:bg-earth-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Daily Card Tab */}
      {activeTab === "daily" && (
        <div>
          <div className="flex justify-center gap-2 mb-8">
            {CATEGORIES.map((cat) => (
              <button key={cat.id} onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)} data-testid={`category-${cat.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${selectedCategory === cat.id ? "border-current" : "border-transparent"}`}
                style={{ backgroundColor: selectedCategory === cat.id ? cat.color + "20" : cat.color + "10", color: cat.color }}>
                {cat.label}
              </button>
            ))}
          </div>

          {card && catInfo && (
            <div className="max-w-lg mx-auto">
              {/* Gradient Card */}
              <div
                ref={cardRef}
                data-testid="affirmation-card"
                className="relative rounded-3xl px-10 py-12 shadow-[0_12px_40px_rgba(45,49,46,0.12)] overflow-hidden"
                style={{ background: catInfo.gradient }}
              >
                {/* Grain texture overlay */}
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundSize: "128px" }} />

                <Star size={28} className="mx-auto mb-6 opacity-60" style={{ color: "white" }} />
                <p className="font-heading text-2xl sm:text-3xl text-white text-center leading-relaxed mb-6">
                  {card.text}
                </p>
                <p className="text-center text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "rgba(255,255,255,0.75)" }}>
                  {card.category}
                </p>
                {/* Sovha watermark */}
                <p className="absolute bottom-4 right-5 font-heading text-sm text-white opacity-50">Sovha</p>
              </div>

              {/* Actions */}
              <div className="flex justify-center gap-3 mt-6">
                <button onClick={saveToFavorites} data-testid="save-favorite-btn"
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${isFaved ? "bg-warmyellow/20 text-warmyellow" : "bg-earth-200/50 text-earth-600 hover:bg-earth-200"}`}>
                  {isFaved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />} {isFaved ? "Saved!" : "Save"}
                </button>
                <button onClick={() => fetchCard(selectedCategory)} data-testid="new-card-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-earth-200/50 text-earth-600 hover:bg-earth-200 transition-all duration-300">
                  <RefreshCw size={16} /> New Card
                </button>
                <button onClick={handleShare} disabled={sharing} data-testid="share-card-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-earth-200/50 text-earth-600 hover:bg-earth-200 transition-all duration-300 disabled:opacity-50">
                  <Share2 size={16} /> {sharing ? "Saving..." : "Share"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Favorites Tab */}
      {activeTab === "favorites" && (
        <div data-testid="favorites-list">
          {favorites.length === 0 ? (
            <div className="text-center py-16"><Bookmark size={40} className="mx-auto text-earth-300 mb-4" /><p className="text-earth-500">No favorites yet. Save affirmations that speak to you.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {favorites.map((f) => {
                const cat = CATEGORIES.find((c) => c.id === f.category);
                return (
                  <div key={f.id} className="rounded-2xl p-6 shadow-[0_4px_20px_rgba(45,49,46,0.06)] relative group" style={{ background: cat?.gradient || "white" }}>
                    <button onClick={() => removeFavorite(f.id)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-white/60 hover:text-white transition-all"><X size={14} /></button>
                    <p className="font-heading text-lg text-white mb-3">{f.text}</p>
                    <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "rgba(255,255,255,0.7)" }}>{f.category}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Share Warning Modal */}
      {showShareWarning && (
        <div className="fixed inset-0 z-[70] bg-earth-800/40 flex items-center justify-center px-4" data-testid="share-warning-modal">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-warmyellow/15 flex items-center justify-center"><AlertTriangle size={20} className="text-warmyellow" /></div>
              <h3 className="font-heading text-lg text-earth-800">Heads up</h3>
            </div>
            <p className="text-earth-600 text-sm leading-relaxed mb-6">Sharing from this app may be visible to others on your device or network. Only share if you feel safe doing so.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowShareWarning(false)} data-testid="share-cancel-btn" className="flex-1 border border-earth-300 text-earth-700 rounded-full py-2.5 font-medium text-sm hover:bg-earth-100 transition-all">Cancel</button>
              <button onClick={confirmShare} data-testid="share-confirm-btn" className="flex-1 bg-terracotta text-earth-50 rounded-full py-2.5 font-medium text-sm hover:-translate-y-0.5 hover:shadow-md transition-all">Share anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
